import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import Sidebar from "../Components/Sidebar";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import {
  campaigns,
  fetchAllCampaignServices,
  fetchCampaignEditData,
} from "../Services/APIService";
import { useNavigate, useLocation } from "react-router-dom";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import CreateCampaignModal from "../Components/CreateCampaignDemo";
import EditCampaignModal from "../Components/EditCampaignDemo";
import { Toaster, toast } from "react-hot-toast";
import OverlayLoader from "../Components/OverlayLoader";

interface Campaigns {
  campaignId: number;
  tenantId?: number;
  campaignName: string;
  description: string;
  fromDate: Date;
  toDate: Date;
  mediaId: number;
  mediaSourceName: string;
  subMediumId: number;
  subMediumSourceName: string;
  mediaIdentifierKey: string;
  campaignImage: string;
  isActive: boolean | string;
}

interface UpdateCampaign {
  campaignId: number;
  campaignName: string;
  description: string;
  fromDate: Date;
  toDate: Date;
  isActive: boolean;
  mediaId: number;
  subMediumId: number;
  mediaIdentifierKey: string;
  campaignImage: string;
  modifiedBy: number;
  isAssignTabShown?: boolean;
}

const formatDatetime = (date: Date | string): string => {
  const dateObj =
    typeof date === "string"
      ? new Date(date.endsWith("Z") ? date : `${date}Z`)
      : date;

  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
    return "Invalid Date";
  }

  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const year = dateObj.getFullYear();

  let hours = dateObj.getHours();
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const hoursStr = String(hours).padStart(2, "0");

  return `${day}-${month}-${year} ${hoursStr}:${minutes} ${ampm}`;
};

const formatOnlyDate = (date: Date | string): string => {
  const dateObj =
    typeof date === "string"
      ? new Date(date.endsWith("Z") ? date : `${date}Z`)
      : date;

  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
    return "Invalid Date";
  }

  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const year = dateObj.getFullYear();

  let hours = dateObj.getHours();
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const hoursStr = String(hours).padStart(2, "0");

  return `${day}-${month}-${year}`;
};

const CampaignTable: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [campaign, setCampaign] = useState<Campaigns[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>("");

  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isImageModalOpen, setImageModalOpen] = useState(false);

  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(
    null
  );
  const [selectedCampaignName, setSelectedCampaignName] = useState<
    string | null
  >(null);
  const [modalImage, setModalImage] = useState<string | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [selectedRows, setSelectedRows] = useState<Campaigns[]>([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [userType, setUserType] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [editCampaignData, setEditCampaignData] =
    useState<UpdateCampaign | null>(null);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const fetchCampaignServices = async (): Promise<void> => {
    try {
      const allCampaignServices = await fetchAllCampaignServices();
      setCampaign(allCampaignServices);
      setLoading(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaignServices();
    const storedUserType = localStorage.getItem("userTypeId");
    setUserType(storedUserType);
  }, []);

  useEffect(() => {
    if (location.state?.refresh) {
      fetchCampaignServices();
      navigate(location.pathname, { replace: true });
    }

    if (location.state?.message) {
      toast.success(location.state.message);
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  const filteredCampaign = campaign.filter((c) => {
    return (
      (c.campaignName?.toLowerCase() || "").includes(
        searchText.toLowerCase()
      ) ||
      (c.mediaSourceName?.toLowerCase() || "").includes(
        searchText.toLowerCase()
      ) ||
      (c.subMediumSourceName?.toLowerCase() || "").includes(
        searchText.toLowerCase()
      )
    );
  });

  const handleCreateContact = (campaignData: any) => {
    alert(`Contact created: ${campaignData.campaignName}`);
    setCreateModalOpen(false);
  };

  const handleImageClick = async (campaignId: number) => {
    setSelectedCampaignId(campaignId);
    const selectedCampaign = campaign.find((c) => c.campaignId === campaignId);
    setSelectedCampaignName(
      selectedCampaign ? selectedCampaign.campaignName : null
    );

    setModalLoading(true);
    setModalError(null);
    setModalImage(null);
    setZoomLevel(1);
    setImageModalOpen(true);

    try {
      const response = await fetchCampaignEditData(campaignId);
      if (response && response.campaignImage) {
        setModalImage(response.campaignImage);
      } else {
        setModalError("No image found for this campaign");
      }
    } catch (error) {
      console.error("Error fetching campaign image:", error);
      setModalError("Failed to fetch campaign image");
    } finally {
      setModalLoading(false);
    }
  };

  const downloadImage = () => {
    if (!modalImage) return;
    const campaignName = selectedCampaignName || "campaign_image";
    const sanitizedName = campaignName
      .replace(/[\\/:*?"<>|]/g, "")
      .replace(/\s+/g, "_")
      .trim();
    const fileName = sanitizedName
      ? `${sanitizedName}.png`
      : `campaign_image_${Date.now()}.png`;

    const link = document.createElement("a");
    link.href = `data:image/png;base64,${modalImage}`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleMouseEnter = () => setZoomLevel(1.5);
  const handleMouseLeave = () => setZoomLevel(1);

  const handleCreateSuccess = (data: UpdateCampaign) => {
    // Open update card with the newly created data
    const dataWithTabFlag = {
      ...data,
      isAssignTabShown: true,
    };
    setEditCampaignData(dataWithTabFlag);
    setEditModalOpen(true);
    setCreateModalOpen(false);
  };

  const handleEdit = async (campaignId: number) => {
    try {
      const response = await fetchCampaignEditData(campaignId);
      if (response) {
        setEditCampaignData({ ...response, isAssignTabShown: false });
        setEditModalOpen(true);
      } else {
        throw new Error("No campaign data found");
      }
    } catch (error) {
      console.error("Error fetching campaign edit data:", error);
      alert("Failed to fetch campaign data for editing");
    }
  };
  const handleRowSelected = (state: { selectedRows: Campaigns[] }) => {
    setSelectedRows(state.selectedRows);
  };

  const columns = [
    {
      name: "Name",
      selector: (row: Campaigns) => row.campaignName ?? "N/A",
      sortable: true,
      cell: (row: Campaigns) => (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: "24px",
              height: "24px",
              borderRadius: "50%",
              backgroundColor: "rgb(167 133 144)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "10px",
              fontWeight: "bold",
            }}
          >
            {row.campaignName.charAt(0).toUpperCase()}
          </div>
          <span style={{ fontWeight: "500", color: "#33475b" }}>
            {row.campaignName}
          </span>
        </div>
      ),
      width: "200px",
    },
    {
      name: "Media",
      cell: (row: Campaigns) => (
        <span
          title={row.mediaSourceName || "N/A"}
          className="hovered-tabledata"
        >
          {row.mediaSourceName || "N/A"}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Submedia",
      cell: (row: Campaigns) => (
        <span
          title={row.subMediumSourceName || "N/A"}
          className="hovered-tabledata"
        >
          {row.subMediumSourceName || "N/A"}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Image",
      cell: (row: Campaigns) => {
        if (!row.campaignImage) return "-";
        return (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleImageClick(row.campaignId);
            }}
          >
            <i className="bi bi-eye"></i>{" "}
          </a>
        );
      },
      width: "80px",
    },

    {
      name: "Status",
      selector: (row: campaigns) => row.isActive,
      sortable: true,
      cell: (row: campaigns) => {
        const isActive =
          typeof row.isActive === "boolean"
            ? row.isActive
            : row.isActive === "true";
        return (
          <span className={isActive ? "active-status" : "inactive-status"}>
            {isActive ? "ACTIVE" : "INACTIVE"}
          </span>
        );
      },
      width: "100px",
    },

    {
      name: "From Date",
      cell: (row: Campaigns) => (
        <span
          title={formatDatetime(row.fromDate)}
          className="hovered-tabledata"
        >
          {formatOnlyDate(row.fromDate)}
        </span>
      ),
      sortable: true,
      sortFunction: (rowA: Campaigns, rowB: Campaigns) =>
        new Date(rowA.fromDate).getTime() - new Date(rowB.fromDate).getTime(),
      width: "120px",
    },
    {
      name: "To Date",
      cell: (row: Campaigns) => (
        <span title={formatDatetime(row.toDate)} className="hovered-tabledata">
          {formatOnlyDate(row.toDate)}
        </span>
      ),
      sortable: true,
      sortFunction: (rowA: Campaigns, rowB: Campaigns) =>
        new Date(rowA.toDate).getTime() - new Date(rowB.toDate).getTime(),
      width: "100px",
    },
    {
      name: "Description",
      cell: (row: Campaigns) => (
        <span title={row.description || "N/A"} className="hovered-tabledata">
          {row.description || "N/A"}
        </span>
      ),
      sortable: true,
    },
    {
      name: "",
      cell: (row: Campaigns) => (
        <div className="action-cell">
          {userType !== "4" && (
            <button
              className="action-button edit-button"
              title="Edit Campaign"
              onClick={() => handleEdit(row.campaignId)}
            >
              <i className="bi bi-pencil-square"></i>
            </button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "60px",
    },
  ];
  if (error) return <div className="p-4 text-red-500">Error: {error}</div>;

  return (
    <div className="wrapper">
      <div>
        <Toaster
          position="top-center"
          reverseOrder={false}
          containerStyle={{
            zIndex: 10000,
          }}
        />
      </div>
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-12 col-lg-12">
                  <div className="card">
                    <div className="card-header carHeaderWithBtn">
                      <div className="card-title mb-0">Campaign List</div>
                      <div>
                        <input
                          type="text"
                          placeholder="Search by Name, Media, or Submedia"
                          className="p-2 w-full datatableSearchInput"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                        />
                        {userType !== "4" && (
                          <button
                            className="demo-btn btn btn-outline-primary btn-sm"
                            onClick={() => setCreateModalOpen(true)}
                          >
                            + Add Campaign
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="card-body p-0">
                      <DataTable
                        columns={columns}
                        data={filteredCampaign}
                        pagination
                        paginationPerPage={10}
                        highlightOnHover
                        striped
                        responsive
                        noDataComponent="No campaigns found"
                        selectableRows
                        selectableRowsComponentProps={{
                          className: "custom-checkbox",
                        }}
                        onSelectedRowsChange={handleRowSelected}
                        clearSelectedRows={toggleCleared}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Create Campaign Modal */}
            <CreateCampaignModal
              isOpen={isCreateModalOpen}
              onClose={() => setCreateModalOpen(false)}
              onSubmit={handleCreateContact}
              onSuccess={handleCreateSuccess}
            />

            {/* Image Modal */}
            {isImageModalOpen && (
              <div
                className="modal"
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0,0,0,0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 9999,
                  padding: "10px",
                  boxSizing: "border-box",
                }}
                role="dialog"
                aria-labelledby="modal-title"
                aria-modal="true"
              >
                <div
                  className="modal-content"
                  style={{
                    backgroundColor: "white",
                    padding: "15px",
                    position: "relative",
                    width: "90vw",
                    maxWidth: "800px",
                    maxHeight: "90vh",
                    overflowY: "auto",
                    borderRadius: "8px",
                    textAlign: "center",
                    boxSizing: "border-box",
                  }}
                >
                  <button
                    className="close"
                    style={{
                      position: "absolute",
                      top: "10px",
                      right: "15px",
                      cursor: "pointer",
                      fontSize: "24px",
                      lineHeight: "24px",
                      border: "none",
                      background: "transparent",
                    }}
                    onClick={() => setImageModalOpen(false)}
                    aria-label="Close modal"
                  >
                    ×
                  </button>

                  <div style={{ marginBottom: "10px", marginTop: "18px" }}>
                    {selectedCampaignName && (
                      <h3
                        id="modal-title"
                        style={{
                          fontSize: "1.2rem",
                          margin: "0 0 5px 0",
                          wordBreak: "break-word",
                        }}
                      >
                        {selectedCampaignName}
                      </h3>
                    )}
                  </div>

                  {modalLoading ? (
                    <div>Loading image...</div>
                  ) : modalError ? (
                    <div>{modalError}</div>
                  ) : modalImage ? (
                    <>
                      <img
                        src={`data:image/png;base64,${modalImage}`}
                        alt={`Campaign ${selectedCampaignName}`}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                          transform: `scale(${zoomLevel})`,
                          transition: "transform 0.3s ease",
                        }}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                      />
                      <div style={{ marginTop: "10px" }}>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={downloadImage}
                        >
                          Download Image
                        </button>
                      </div>
                    </>
                  ) : (
                    <div>No image found</div>
                  )}
                </div>
              </div>
            )}

            {/* Edit Campaign Modal */}
            {isEditModalOpen && editCampaignData && (
              <EditCampaignModal
                isOpen={isEditModalOpen}
                onClose={() => {
                  setEditModalOpen(false);
                  setEditCampaignData(null);
                }}
                campaignData={editCampaignData}
                onSubmit={async (updatedCampaign) => {
                  setEditModalOpen(false);
                  setEditCampaignData(null);
                  toast.success("Campaign updated successfully");
                  await fetchCampaignServices();
                }}
              />
            )}
          </main>
        </div>
        <Footer />
      </div>
      <OverlayLoader
        loading={loading}
        message="Loading Campaign Data"
        submessage="This might take a moment..."
      />
    </div>
  );
};

export default CampaignTable;
